import React from 'react';
import './AppPricingBadge.css';

/**
 * SINGLE SOURCE OF TRUTH = the backend.
 *
 * Every catalog payload (marketplace list + `GET /apps/catalog/:type`)
 * already carries a resolved `pricing` block from the backend's
 * pricing-rates.js SSOT:
 *
 *   pricing = { tier, ratePerMinuteMilliCents, ratePerMinuteUsd, ratePerHourUsd }
 *
 * resolveTier() below PREFERS that live rate. The hardcoded table here is
 * ONLY a cold-load fallback (so the badge isn't blank before the catalog
 * fetch resolves) and mirrors the current backend numbers — but the live
 * `pricing.ratePerMinuteMilliCents` always wins when present, so the two can
 * never drift in what the user actually sees.
 *
 * Backend rates (pricing-rates.js, cost+15%, right-sized):
 *   light    0.5 vCPU / 1 GB  ≈ $0.0284/hr  (~$20/mo if always on)
 *   standard 1   vCPU / 2 GB  ≈ $0.0567/hr  (~$41/mo if always on)
 *   heavy    DEPRECATED — aliases standard (billed at the 1 vCPU rate even
 *            though a stray catalog ref may still size the task bigger).
 */
export const TIER_FALLBACKS = {
  light:    { label: 'Light',    milliCentsPerMin:  47 },  // ≈ $0.0284/hr
  standard: { label: 'Standard', milliCentsPerMin:  95 },  // ≈ $0.0567/hr
  heavy:    { label: 'Standard', milliCentsPerMin:  95 },  // deprecated alias → standard
  xl:       { label: 'XL',       milliCentsPerMin: 140 },  // 1vCPU/8GB ≈ $0.084/hr
};

const TIER_TO_VARIANT = {
  light: 'green',
  standard: 'blue',
  heavy: 'blue', // deprecated alias → standard (was orange; never a separate price now)
  xl: 'blue',
};

function fmtUsd(usd) {
  // Sub-dime rates need 3 decimals so $0.0284 doesn't collapse to "$0.03".
  return usd < 0.1 ? `$${usd.toFixed(3)}` : `$${usd.toFixed(2)}`;
}

function formatHourly(milliCentsPerMin) {
  return `${fmtUsd((milliCentsPerMin * 60) / 100000)}/hr`;
}

/**
 * Resolve a tier → display info. Accepts EITHER:
 *   - a `pricing` object  { tier, ratePerMinuteMilliCents, ... }  (preferred —
 *     uses the live backend rate), or
 *   - a bare tier string  'light' | 'standard' | 'heavy'          (fallback
 *     table only).
 *
 * The hourly string + monthly estimate are always DERIVED from whichever
 * milli-cents/min won, so the pill, the tooltip, and the backend charge stay
 * in lockstep.
 */
export function resolveTier(tierOrPricing) {
  const pricing = (tierOrPricing && typeof tierOrPricing === 'object') ? tierOrPricing : null;
  const tier = pricing ? pricing.tier : tierOrPricing;
  const key = String(tier || 'standard').toLowerCase();
  const fb = TIER_FALLBACKS[key] || TIER_FALLBACKS.standard;
  // Live backend rate wins; fall back to the table only when absent.
  const liveMc = Number(pricing?.ratePerMinuteMilliCents);
  const milliCentsPerMin = Number.isFinite(liveMc) && liveMc >= 0
    ? liveMc
    : fb.milliCentsPerMin;
  return {
    key,
    label: fb.label,
    milliCentsPerMin,
    hourly: formatHourly(milliCentsPerMin),
    variant: TIER_TO_VARIANT[key] || 'blue',
  };
}

/**
 * Approximate monthly cost if the app runs 24/7. Returns a formatted
 * $X/mo string. Used in the hover tooltip.
 */
function approxMonthly(milliCentsPerMin) {
  const hours = 24 * 30; // ≈ 720 hours
  const usd = (milliCentsPerMin * 60 * hours) / 100000;
  return `$${usd.toFixed(2)}/mo`;
}

/**
 * AppPricingBadge — small pill rendered on each marketplace card.
 *
 * Pass `pricing` (the catalog entry's resolved pricing block) so the badge
 * shows the live backend rate; `tier` is accepted as a string fallback for
 * callers that only have the tier name.
 *
 * Variants (driven by tier): green / blue.
 * Hover: shows "≈ $X per month if always on" via native title attribute.
 */
const AppPricingBadge = ({ tier, pricing, className = '' }) => {
  const info = resolveTier(pricing || tier);
  const tooltip = `${info.label} • ${info.hourly} · ≈ ${approxMonthly(info.milliCentsPerMin)} if always on`;
  return (
    <span className={`app-pricing-badge ${className}`.trim()} title={tooltip}>
      {info.hourly}
    </span>
  );
};

export default AppPricingBadge;
